const LIGHT_STATUS_BAR_COLOR = '#2563EB';
const DARK_STATUS_BAR_COLOR = '#4F8CFF';

function setNativeSafeAreaTop(height) {
  if (typeof document === 'undefined') {
    return;
  }

  const normalizedHeight = Number.isFinite(height) ? Math.max(0, Math.round(height)) : 0;
  document.documentElement.style.setProperty('--native-safe-area-top', `${normalizedHeight}px`);
}

function normalizeTheme(theme) {
  return theme === 'dark' ? 'dark' : 'light';
}

export function applyThemeClasses(theme) {
  if (typeof document === 'undefined') {
    return normalizeTheme(theme);
  }

  const normalizedTheme = normalizeTheme(theme);
  const themeClass = normalizedTheme === 'dark' ? 'dark' : 'light';
  const legacyThemeClass = normalizedTheme === 'dark' ? 'dark-theme' : 'light-theme';

  try {
    localStorage.setItem('themeMode', normalizedTheme);
    localStorage.setItem('theme', normalizedTheme);
  } catch (error) {
    // ignore storage issues
  }

  document.body.classList.remove('dark-theme', 'light-theme', 'dark', 'light');
  document.documentElement.classList.remove('dark-theme', 'light-theme', 'dark', 'light');
  document.body.classList.add(themeClass, legacyThemeClass);
  document.documentElement.classList.add(themeClass, legacyThemeClass);

  return normalizedTheme;
}

export function getCurrentThemeIsDark() {
  if (typeof document === 'undefined') {
    return false;
  }

  const doc = document.documentElement;
  const body = document.body;

  const hasDarkThemeClass = doc.classList.contains('dark-theme') || body.classList.contains('dark-theme');
  const hasLightThemeClass = doc.classList.contains('light-theme') || body.classList.contains('light-theme');
  const hasDarkClass = doc.classList.contains('dark') || body.classList.contains('dark');

  // Prefer explicit class presence (dark / dark-theme / light-theme). Only
  // fall back to stored preference when no explicit classes are set.
  if (hasDarkThemeClass || hasDarkClass) return true;
  if (hasLightThemeClass) return false;

  const storedTheme = (() => {
    try {
      return localStorage.getItem('themeMode') || localStorage.getItem('theme');
    } catch (err) {
      return null;
    }
  })();

  return storedTheme === 'dark';
}

export async function syncNativeStatusBar(theme, options = {}) {
  if (typeof window === 'undefined' || !window.Capacitor) {
    return;
  }

  try {
    const mod = await import('@capacitor/status-bar');
    const { StatusBar, Style } = mod;
    const normalizedTheme = normalizeTheme(theme ?? (getCurrentThemeIsDark() ? 'dark' : 'light'));
    const isDarkMode = normalizedTheme === 'dark';
    const isAndroid = window.Capacitor?.getPlatform?.() === 'android';
    const requestedBackgroundColor = typeof options.backgroundColor === 'string'
      ? options.backgroundColor
      : isDarkMode
        ? DARK_STATUS_BAR_COLOR
        : LIGHT_STATUS_BAR_COLOR;

    // Use immersive overlay so the app header can visually continue into the status bar area.
    // Some Android versions/OEMs may not support this call; keep sync resilient.
    if (isAndroid) {
      try {
        await StatusBar.setOverlaysWebView({ overlay: true });
      } catch (error) {
        // Not available on all Android versions.
      }
    }

    try {
      await StatusBar.setBackgroundColor({ color: requestedBackgroundColor });
    } catch (error) {
      // Not available on all Android versions.
    }

    try {
      let statusBarStyle = isDarkMode ? Style.Dark : Style.Light;
      if (options.iconStyle === 'light') {
        statusBarStyle = Style.Dark;
      } else if (options.iconStyle === 'dark') {
        statusBarStyle = Style.Light;
      }
      await StatusBar.setStyle({ style: statusBarStyle });
    } catch (error) {
      // Ignore style sync failures on unsupported environments.
    }

    try {
      const info = await StatusBar.getInfo();
      setNativeSafeAreaTop(info?.height ?? 0);
    } catch (error) {
      setNativeSafeAreaTop(0);
    }
  } catch (error) {
    // plugin unavailable or failed; ignore on web
  }
}

function enableThemeTransition() {
  if (typeof document === 'undefined') {
    return;
  }

  const html = document.documentElement;
  html.classList.add('theme-transition');
  window.setTimeout(() => html.classList.remove('theme-transition'), 350);
}

export async function syncTheme(theme) {
  enableThemeTransition();

  return new Promise((resolve) => {
    requestAnimationFrame(async () => {
      const normalizedTheme = applyThemeClasses(theme);
      await syncNativeStatusBar(normalizedTheme);
      resolve(normalizedTheme);
    });
  });
}